import config from '../config';

const getToken = () => localStorage.getItem('vt_dashboard_token');

const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  if (!token) throw new Error('No authentication token found');

  const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.error || errorBody.message || errorMessage;
    } catch {
      const errorText = await response.text().catch(() => '');
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  // Some endpoints answer with plain text (e.g. Jumio disconnect).
  const text = await response.text();
  try { return text ? JSON.parse(text) : null; } catch { return text; }
};

export const api = {
  // Auth
  login: (email, password) =>
    fetch(`${config.API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }).then(r => r.json()),

  register: (email, password, firstName) =>
    fetch(`${config.API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName }),
    }).then(r => r.json()),

  // User
  getUserProfile: () => apiRequest('/api/user'),

  // Wallet hub config
  getVerificationConfig: () => apiRequest('/api/verification-config'),
  updateVerificationConfig: (config) =>
    apiRequest('/api/verification-config', { method: 'POST', body: JSON.stringify(config) }),

  // EUDI allowed attributes (from registration cert)
  getAllowedPidAttributes: () => apiRequest('/api/eudi/allowed-attributes'),

  // Wallet ecosystems
  getWalletEcosystems: () => apiRequest('/api/wallets/ecosystems'),
  previewWalletRequest: (walletId, body) =>
    apiRequest(`/api/wallets/${walletId}/request-preview`, { method: 'POST', body: JSON.stringify(body || {}) }),

  // API keys
  getApiKeys: () => apiRequest('/api/keys'),
  generateApiKeys: () => apiRequest('/api/keys/generate', { method: 'POST' }),

  // Audit logs
  getAuditLogs: (page = 0) => apiRequest(`/api/v1/audit-logs?page=${page}`),

  // Stats
  getStats: () => apiRequest('/api/v1/stats'),

  // Integrations
  getJumioConfig: () => apiRequest('/integrations/jumio'),
  saveJumioConfig: (cfg) =>
    apiRequest('/integrations/jumio', { method: 'POST', body: JSON.stringify(cfg) }),
  deleteJumioConfig: () =>
    apiRequest('/integrations/jumio', { method: 'DELETE' }),

  // Google Wallet merchant verifier certificate (X.509 leaf for signed OpenID4VP)
  getGoogleWalletCertificate: () => apiRequest('/api/google-wallet/certificate'),
  issueGoogleWalletCertificate: (body) =>
    apiRequest('/api/google-wallet/certificate', { method: 'POST', body: JSON.stringify(body) }),
  revokeGoogleWalletCertificate: () =>
    apiRequest('/api/google-wallet/certificate', { method: 'DELETE' }),
};

export default api;
